import { usePageTitle } from "@/hooks/use-page-title";
import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useDebounce } from "@/hooks/use-debounce";
import { useMutation, useInfiniteQuery, keepPreviousData } from "@tanstack/react-query";
import { Link, useLocation, useSearch } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Eye, Ban, ExternalLink, Loader2, Search, ArrowUpDown, ArrowUp, ArrowDown, Calendar as CalendarIcon, RefreshCw, Mail } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import ResponsiveLayout from "@/components/layout/responsive-layout";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { WatchlistDetailModal } from "@/components/modals/watchlist-detail-modal";
import { format, startOfDay, startOfWeek, startOfMonth, startOfYear, isWithinInterval, endOfDay } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import type { Watchlist } from "@shared/schema";

type WatchlistWithWorkOrder = Watchlist & {
  workOrder: {
    id: number;
    workOrderNumber: string;
    serviceType: string;
    companyId: number;
    isPortalRequest: boolean;
  } | null;
  carrier: {
    id: number;
    name: string;
  } | null;
};

type DateRangeOption = "all" | "today" | "thisWeek" | "thisMonth" | "thisYear" | "noMatches" | "custom";

const dateRangeOptions: { value: DateRangeOption; label: string }[] = [
  { value: "all", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "thisWeek", label: "This Week" },
  { value: "thisMonth", label: "This Month" },
  { value: "thisYear", label: "This Year" },
  { value: "noMatches", label: "No Matches Yet" },
  { value: "custom", label: "Custom Range" },
];

function getDateRangeStart(option: DateRangeOption): Date | null {
  const now = new Date();
  switch (option) {
    case "today":
      return startOfDay(now);
    case "thisWeek":
      return startOfWeek(now, { weekStartsOn: 1 });
    case "thisMonth":
      return startOfMonth(now);
    case "thisYear":
      return startOfYear(now);
    default:
      return null;
  }
}

const statusColors: Record<string, string> = {
  active: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
  matched: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  completed: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  expired: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300",
  canceled: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
};

interface ColumnConfig {
  id: string;
  label: string;
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
}

const COLUMN_CONFIG: ColumnConfig[] = [
  { id: 'identifier', label: 'Identifier', defaultWidth: 180, minWidth: 120, maxWidth: 300 },
  { id: 'workOrder', label: 'Work Order', defaultWidth: 100, minWidth: 80, maxWidth: 160 },
  { id: 'type', label: 'Type', defaultWidth: 100, minWidth: 70, maxWidth: 150 },
  { id: 'letterType', label: 'Letter Type', defaultWidth: 110, minWidth: 80, maxWidth: 180 },
  { id: 'status', label: 'Status', defaultWidth: 100, minWidth: 80, maxWidth: 140 },
  { id: 'matches', label: 'Matches', defaultWidth: 80, minWidth: 60, maxWidth: 120 },
  { id: 'age', label: 'Age', defaultWidth: 80, minWidth: 60, maxWidth: 120 },
  { id: 'lastMatched', label: 'Last Matched', defaultWidth: 100, minWidth: 80, maxWidth: 150 },
  { id: 'created', label: 'Created', defaultWidth: 90, minWidth: 70, maxWidth: 140 },
  { id: 'actions', label: '', defaultWidth: 50, minWidth: 40, maxWidth: 80 },
];

const STORAGE_KEY = 'watchlists-column-widths';

function getStoredColumnWidths(): Record<string, number> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return {};
}

function saveColumnWidths(widths: Record<string, number>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(widths));
  } catch {}
}

type SortField = "identifier" | "status" | "type" | "matchCount" | "lastMatched" | "created" | "age";
type SortDirection = "asc" | "desc";

function getWatchlistAge(watchlist: {
  createdAt?: string | Date | null;
  completedAt?: string | Date | null;
  canceledAt?: string | Date | null;
  expiredAt?: string | Date | null;
}): number {
  const created = watchlist.createdAt ? new Date(watchlist.createdAt).getTime() : null;
  if (!created) return 0;
  const now = Date.now();
  const end = Math.min(
    watchlist.completedAt ? new Date(watchlist.completedAt).getTime() : now,
    watchlist.canceledAt  ? new Date(watchlist.canceledAt).getTime()  : now,
    watchlist.expiredAt   ? new Date(watchlist.expiredAt).getTime()   : now,
    now
  );
  return Math.round((end - created) / 86_400_000);
}

export default function WatchlistsPage() {
  usePageTitle("Watchlists");
  const { toast } = useToast();
  const [location, setLocation] = useLocation();
  const searchString = useSearch();
  const [selectedWatchlist, setSelectedWatchlist] = useState<WatchlistWithWorkOrder | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 250);
  const [statusFilter, setStatusFilter] = useState<string>(() => {
    return new URLSearchParams(window.location.search).get("status") || "all";
  });
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [dateRangeFilter, setDateRangeFilter] = useState<DateRangeOption>(() => {
    return new URLSearchParams(window.location.search).get("noMatches") === "true" ? "noMatches" : "all";
  });
  const [customDateFrom, setCustomDateFrom] = useState<Date | undefined>(undefined);
  const [customDateTo, setCustomDateTo] = useState<Date | undefined>(undefined);
  const [sortField, setSortField] = useState<SortField>("created");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [ageRangeFilter, setAgeRangeFilter] = useState<string>(() => {
    return new URLSearchParams(window.location.search).get("ageBucket") || "all";
  });
  const [requestorFilter, setRequestorFilter] = useState<string>("all");

  useEffect(() => {
    const params = new URLSearchParams(searchString);
    const status = params.get("status");
    const noMatches = params.get("noMatches");
    const ageBucket = params.get("ageBucket");
    setStatusFilter(status || "all");
    if (noMatches === "true") setDateRangeFilter("noMatches");
    setAgeRangeFilter(ageBucket || "all");
  }, [searchString]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (dateRangeFilter === 'noMatches') params.set('noMatches', 'true');
    if (ageRangeFilter !== 'all') params.set('ageBucket', ageRangeFilter);
    const newSearch = params.toString();
    if (newSearch !== searchString) {
      setLocation(`${location}${newSearch ? `?${newSearch}` : ''}`, { replace: true } as any);
    }
  }, [statusFilter, dateRangeFilter, ageRangeFilter, searchString, location, setLocation]);

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    const stored = getStoredColumnWidths();
    const defaults: Record<string, number> = {};
    COLUMN_CONFIG.forEach(col => {
      defaults[col.id] = stored[col.id] || col.defaultWidth;
    });
    return defaults;
  });

  const resizingRef = useRef<{ columnId: string; startX: number; startWidth: number } | null>(null);

  const handleResizeStart = useCallback((columnId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startWidth = columnWidths[columnId] || COLUMN_CONFIG.find(c => c.id === columnId)?.defaultWidth || 100;
    resizingRef.current = { columnId, startX: e.clientX, startWidth };
    
    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!resizingRef.current) return;
      const config = COLUMN_CONFIG.find(c => c.id === resizingRef.current!.columnId);
      if (!config) return;
      
      const diff = moveEvent.clientX - resizingRef.current.startX;
      const newWidth = Math.max(
        config.minWidth,
        Math.min(config.maxWidth, resizingRef.current.startWidth + diff)
      );
      
      setColumnWidths(prev => ({
        ...prev,
        [resizingRef.current!.columnId]: newWidth
      }));
    };
    
    const handleMouseUp = () => {
      if (resizingRef.current) {
        setColumnWidths(prev => {
          saveColumnWidths(prev);
          return prev;
        });
      }
      resizingRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [columnWidths]);

  const getColumnWidth = useCallback((columnId: string) => {
    return columnWidths[columnId] || COLUMN_CONFIG.find(c => c.id === columnId)?.defaultWidth || 100;
  }, [columnWidths]);

  const computedDateFrom = useMemo(() => {
    if (dateRangeFilter === 'all' || dateRangeFilter === 'noMatches') return undefined;
    if (dateRangeFilter === 'custom') return customDateFrom ? customDateFrom.toISOString().split('T')[0] : undefined;
    const start = getDateRangeStart(dateRangeFilter);
    return start ? start.toISOString().split('T')[0] : undefined;
  }, [dateRangeFilter, customDateFrom]);

  const computedDateTo = useMemo(() => {
    if (dateRangeFilter === 'custom') return customDateTo ? customDateTo.toISOString().split('T')[0] : undefined;
    return undefined;
  }, [dateRangeFilter, customDateTo]);

  const {
    data: watchlistsInfinite,
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<{ data: WatchlistWithWorkOrder[]; nextCursor: string | null; totalCount?: number }>({
    queryKey: ["/api/watchlists", {
      search: debouncedSearch,
      status: statusFilter,
      type: typeFilter,
      noMatches: dateRangeFilter === 'noMatches',
      dateFrom: computedDateFrom,
      dateTo: computedDateTo,
      ageBucket: ageRangeFilter,
      requestor: requestorFilter,
    }],
    queryFn: async ({ pageParam }) => {
      const qs = new URLSearchParams();
      if (debouncedSearch.trim()) qs.set('search', debouncedSearch.trim());
      if (statusFilter !== 'all') qs.set('status', statusFilter);
      if (typeFilter !== 'all') qs.set('type', typeFilter);
      if (dateRangeFilter === 'noMatches') qs.set('noMatches', 'true');
      if (computedDateFrom) qs.set('dateFrom', computedDateFrom);
      if (computedDateTo) qs.set('dateTo', computedDateTo);
      if (ageRangeFilter !== 'all') qs.set('ageBucket', ageRangeFilter);
      if (requestorFilter !== 'all') qs.set('requestor', requestorFilter);
      if (pageParam) qs.set('cursor', pageParam as string);
      const res = await fetch(`/api/watchlists${qs.toString() ? `?${qs.toString()}` : ''}`, { credentials: 'include' });
      return res.json();
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    refetchOnMount: "always",
  });

  const watchlists = useMemo(() =>
    watchlistsInfinite?.pages.flatMap(p => p.data) ?? [],
    [watchlistsInfinite]
  );
  const watchlistsTotalCount = watchlistsInfinite?.pages[0]?.totalCount ?? watchlists.length;

  const syncMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/watchlists/sync"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/watchlists"] });
      toast({
        title: "Success",
        description: "Watchlist data synced from Helix",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to sync watchlist data",
        variant: "destructive",
      });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (id: number) => apiRequest("POST", `/api/watchlists/${id}/cancel`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/watchlists"] });
      toast({
        title: "Success",
        description: "Watchlist canceled successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to cancel watchlist",
        variant: "destructive",
      });
    },
  });

  const handleCancel = (id: number) => {
    cancelMutation.mutate(id);
  };

  const sendEmailMutation = useMutation({
    mutationFn: ({ id, emailType }: { id: number; emailType: 'reminder' | 'dismissal' }) =>
      apiRequest("POST", `/api/watchlists/${id}/send-email`, { emailType }).then(r => r.json()),
    onSuccess: (data: any) => {
      const sent = data?.sentCount ?? 0;
      const blocked = data?.blockedCount ?? 0;
      if (sent > 0) {
        toast({ title: "Email sent", description: `Sent to ${sent} recipient(s).` });
      } else if (blocked > 0) {
        toast({ title: "Email blocked (test mode)", description: `Blocked for ${blocked} recipient(s) — domain not in allowed list.`, variant: "destructive" });
      } else {
        toast({ title: "No recipients", description: "No contacts with email addresses found on this work order.", variant: "destructive" });
      }
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to send email.", variant: "destructive" });
    },
  });

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const filteredAndSortedWatchlists = useMemo(() => {
    const sorted = [...watchlists];
    sorted.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case "identifier":
          comparison = (a.targetIdentifier || "").localeCompare(b.targetIdentifier || "");
          break;
        case "status":
          comparison = (a.status || "").localeCompare(b.status || "");
          break;
        case "type":
          comparison = (a.watchlistType || "").localeCompare(b.watchlistType || "");
          break;
        case "matchCount":
          comparison = (a.matchCount || 0) - (b.matchCount || 0);
          break;
        case "age":
          comparison = getWatchlistAge(a) - getWatchlistAge(b);
          break;
        case "lastMatched": {
          const aDate = a.lastMatchedAt ? new Date(a.lastMatchedAt).getTime() : 0;
          const bDate = b.lastMatchedAt ? new Date(b.lastMatchedAt).getTime() : 0;
          comparison = aDate - bDate;
          break;
        }
        case "created":
          comparison =
            new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
          break;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });
    return sorted;
  }, [watchlists, sortField, sortDirection]);

  const getLettersUrl = (watchlist: WatchlistWithWorkOrder) => {
    // Use watchlistId to filter to only letters matched by this specific watchlist
    return `/authority-letters?watchlistId=${watchlist.id}`;
  };

  const SortButton = ({ field, label }: { field: SortField; label: string }) => {
    const isActive = sortField === field;
    const Icon = isActive ? (sortDirection === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <Button
        variant="ghost"
        size="sm"
        className={`h-auto p-0 font-medium hover:bg-transparent ${isActive ? 'text-blue-600' : ''}`}
        onClick={() => toggleSort(field)}
        data-testid={`sort-${field}`}
      >
        {label}
        <Icon className="ml-1 h-3 w-3" />
      </Button>
    );
  };

  if (isLoading) {
    return (
      <ResponsiveLayout title="Watchlists" subtitle="Monitor carriers for new authority letters">
        <div className="p-4">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold tracking-tight">Watchlists</h1>
          </div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 dark:bg-gray-800 animate-pulse rounded-lg"></div>
            ))}
          </div>
        </div>
      </ResponsiveLayout>
    );
  }

  return (
    <ResponsiveLayout title="Watchlists" subtitle="Monitor carriers for new authority letters">
      <div className="p-4">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold tracking-tight">Watchlists</h1>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => syncMutation.mutate()}
                disabled={syncMutation.isPending}
                data-testid="button-sync"
              >
                {syncMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Sync from Helix
                  </>
                )}
              </Button>
              <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="button-refresh">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          <Card>
            <CardHeader>
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search identifier, work order, letter type..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="input-search"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[150px]" data-testid="select-status">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="matched">Matched</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="canceled">Canceled</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-[150px]" data-testid="select-type">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="single">Single Match</SelectItem>
                    <SelectItem value="continuous">Continuous</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={requestorFilter} onValueChange={setRequestorFilter}>
                  <SelectTrigger className="w-[160px]" data-testid="select-requestor">
                    <SelectValue placeholder="All Requestors" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Requestors</SelectItem>
                    <SelectItem value="portal">Portal</SelectItem>
                    <SelectItem value="internal">Internal</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={ageRangeFilter} onValueChange={setAgeRangeFilter}>
                  <SelectTrigger className="w-[140px]" data-testid="select-age-range">
                    <SelectValue placeholder="All Ages" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Ages</SelectItem>
                    <SelectItem value="0-30">0–30 days</SelectItem>
                    <SelectItem value="31-60">31–60 days</SelectItem>
                    <SelectItem value="61-90">61–90 days</SelectItem>
                    <SelectItem value="91-120">91–120 days</SelectItem>
                    <SelectItem value="120+">120+ days</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={dateRangeFilter} onValueChange={(v) => setDateRangeFilter(v as DateRangeOption)}>
                  <SelectTrigger className="w-[170px]" data-testid="select-date-range">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Last Matched" />
                  </SelectTrigger>
                  <SelectContent>
                    {dateRangeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {dateRangeFilter === "custom" && (
                  <div className="flex gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="w-[120px]" data-testid="button-date-from">
                          {customDateFrom ? format(customDateFrom, "MMM d, yyyy") : "From"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={customDateFrom}
                          onSelect={setCustomDateFrom}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="w-[120px]" data-testid="button-date-to">
                          {customDateTo ? format(customDateTo, "MMM d, yyyy") : "To"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={customDateTo}
                          onSelect={setCustomDateTo}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {filteredAndSortedWatchlists.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Eye className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No watchlists found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table className="w-full" style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      {COLUMN_CONFIG.map(col => (
                        <col key={col.id} style={{ width: `${getColumnWidth(col.id)}px` }} />
                      ))}
                    </colgroup>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="relative group" style={{ width: getColumnWidth('identifier') }}>
                          <div className="pr-2"><SortButton field="identifier" label="Identifier" /></div>
                          <div 
                            className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                            onMouseDown={(e) => handleResizeStart('identifier', e)}
                          />
                        </TableHead>
                        <TableHead className="relative group" style={{ width: getColumnWidth('workOrder') }}>
                          <div className="pr-2">Work Order</div>
                          <div 
                            className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                            onMouseDown={(e) => handleResizeStart('workOrder', e)}
                          />
                        </TableHead>
                        <TableHead className="relative group" style={{ width: getColumnWidth('type') }}>
                          <div className="pr-2"><SortButton field="type" label="Type" /></div>
                          <div 
                            className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                            onMouseDown={(e) => handleResizeStart('type', e)}
                          />
                        </TableHead>
                        <TableHead className="relative group" style={{ width: getColumnWidth('letterType') }}>
                          <div className="pr-2">Letter Type</div>
                          <div 
                            className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                            onMouseDown={(e) => handleResizeStart('letterType', e)}
                          />
                        </TableHead>
                        <TableHead className="relative group" style={{ width: getColumnWidth('status') }}>
                          <div className="pr-2"><SortButton field="status" label="Status" /></div>
                          <div 
                            className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                            onMouseDown={(e) => handleResizeStart('status', e)}
                          />
                        </TableHead>
                        <TableHead className="relative group text-center" style={{ width: getColumnWidth('matches') }}>
                          <div className="pr-2"><SortButton field="matchCount" label="Matches" /></div>
                          <div 
                            className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                            onMouseDown={(e) => handleResizeStart('matches', e)}
                          />
                        </TableHead>
                        <TableHead className="relative group" style={{ width: getColumnWidth('age') }}>
                          <div className="pr-2"><SortButton field="age" label="Age" /></div>
                          <div 
                            className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                            onMouseDown={(e) => handleResizeStart('age', e)}
                          />
                        </TableHead>
                        <TableHead className="relative group" style={{ width: getColumnWidth('lastMatched') }}>
                          <div className="pr-2"><SortButton field="lastMatched" label="Last Matched" /></div>
                          <div 
                            className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                            onMouseDown={(e) => handleResizeStart('lastMatched', e)}
                          />
                        </TableHead>
                        <TableHead className="relative group" style={{ width: getColumnWidth('created') }}>
                          <div className="pr-2"><SortButton field="created" label="Created" /></div>
                          <div 
                            className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                            onMouseDown={(e) => handleResizeStart('created', e)}
                          />
                        </TableHead>
                        <TableHead style={{ width: getColumnWidth('actions') }}></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAndSortedWatchlists.map((watchlist) => (
                        <TableRow
                          key={watchlist.id}
                          className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                          onClick={() => {
                            setSelectedWatchlist(watchlist);
                            setIsDetailModalOpen(true);
                          }}
                          data-testid={`row-watchlist-${watchlist.id}`}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Eye className={`w-4 h-4 ${
                                watchlist.status === 'matched' ? 'text-green-500' :
                                watchlist.status === 'completed' ? 'text-blue-500' :
                                watchlist.status === 'expired' ? 'text-gray-400' :
                                watchlist.status === 'canceled' ? 'text-red-500' :
                                'text-amber-500'
                              }`} />
                              <span className="font-medium text-blue-600 dark:text-blue-400">
                                {watchlist.targetIdentifier}
                              </span>
                            </div>
                            {watchlist.carrier && (
                              <span className="text-xs text-gray-500 block mt-1">{watchlist.carrier.name}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {watchlist.workOrder ? (
                              <Link
                                href={`/work-orders/${watchlist.workOrder.id}`}
                                className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`link-work-order-${watchlist.id}`}
                              >
                                {watchlist.workOrder.workOrderNumber}
                                <ExternalLink className="w-3 h-3" />
                              </Link>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">
                              {watchlist.watchlistType === 'single' ? 'Single Match' : 'Continuous'}
                            </span>
                            {watchlist.watchlistDays && (
                              <span className="text-xs text-gray-500 block">{watchlist.watchlistDays} days</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">{watchlist.letterType || "—"}</span>
                          </TableCell>
                          <TableCell>
                            <Badge className={statusColors[watchlist.status] || statusColors.active}>
                              {watchlist.status.charAt(0).toUpperCase() + watchlist.status.slice(1)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            {(watchlist.matchCount || 0) > 0 ? (
                              <Link
                                href={getLettersUrl(watchlist)}
                                className="font-semibold text-green-600 hover:underline cursor-pointer"
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`link-matches-${watchlist.id}`}
                              >
                                {watchlist.matchCount}
                              </Link>
                            ) : (
                              <span className="font-semibold text-gray-400">0</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-gray-600">{getWatchlistAge(watchlist)} d</span>
                          </TableCell>
                          <TableCell>
                            {watchlist.lastMatchedAt ? (
                              <span className="text-sm">
                                {format(new Date(watchlist.lastMatchedAt), "MMM d, ''yy")}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm">Never</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-gray-600">
                              {watchlist.createdAt
                                ? format(new Date(watchlist.createdAt), "MMM d, ''yy")
                                : "—"}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const emailType = ['canceled', 'expired'].includes(watchlist.status)
                                    ? 'dismissal'
                                    : 'reminder';
                                  sendEmailMutation.mutate({ id: watchlist.id, emailType });
                                }}
                                disabled={sendEmailMutation.isPending}
                                title={['canceled', 'expired'].includes(watchlist.status) ? 'Send dismissal email' : 'Send reminder email'}
                                data-testid={`button-send-email-${watchlist.id}`}
                              >
                                {sendEmailMutation.isPending ? (
                                  <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                                ) : (
                                  <Mail className="h-4 w-4 text-blue-500" />
                                )}
                              </Button>
                            {!['canceled', 'completed'].includes(watchlist.status) && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => e.stopPropagation()}
                                  data-testid={`button-cancel-${watchlist.id}`}
                                  title="Cancel watchlist"
                                >
                                  <Ban className="h-4 w-4 text-orange-500" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Cancel Watchlist</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to cancel this watchlist? This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>No, keep it</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleCancel(watchlist.id)}
                                    className="bg-orange-500 hover:bg-orange-600"
                                  >
                                    {cancelMutation.isPending ? (
                                      <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Canceling...
                                      </>
                                    ) : (
                                      "Yes, cancel it"
                                    )}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                            )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="flex flex-col items-center gap-2 mt-4 px-4 pb-4">
        <p className="text-sm text-muted-foreground" aria-live="polite">
          Showing {watchlists.length} of {watchlistsTotalCount}
        </p>
        {hasNextPage && (
          <Button variant="outline" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}
            aria-label="Load more watchlists" aria-busy={isFetchingNextPage}>
            {isFetchingNextPage ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</>
            ) : "Load more"}
          </Button>
        )}
      </div>

      <WatchlistDetailModal
        watchlist={selectedWatchlist}
        open={isDetailModalOpen}
        onOpenChange={setIsDetailModalOpen}
      />
    </ResponsiveLayout>
  );
}
